/**
 * lookTargetRuntime — the ONE place that says where the character looks.
 *
 * Head Follow and Eye Follow used to each measure "direction to the camera" on
 * their own. That is fine while the camera is the only thing worth looking at,
 * but anything that wants the character to look somewhere ELSE (glance around,
 * break eye contact, hand the head back to the animation) would otherwise have
 * to be wired into both systems separately and kept in step by hand. This
 * module is the shared channel instead — Unreal's equivalent is the single
 * `LookTargetFinal` that feeds the body Control Rig and both face ABPs.
 *
 * Two halves, both plain data so the module stays THREE-free and testable
 * under `node --test`:
 *
 *   provider — written once per frame by whatever decides where to look (e.g.
 *              a face-graph node). Consumers ignore it once it goes stale, so
 *              deleting that node returns everything to plain camera-tracking
 *              with nothing left latched.
 *   scene    — written once per frame by the renderer (eyeFollowScene), so a
 *              provider can see where the camera is relative to the character
 *              without touching THREE or the scene graph itself.
 *
 * With no provider, `gate()` is 1 and both resolve helpers return the camera
 * direction unchanged — existing graphs behave exactly as before.
 *
 * Angle conventions match eyeAim.js / headFollowRuntime: yaw is atan2(x, z)
 * with +Z rest-forward, so +yaw is toward the character's LEFT; +pitch is up.
 * Provider yaw/pitch are BODY-RELATIVE (radians) — "25° to the left of where the
 * torso faces" — so a glance stays in front of the character when it turns.
 */

import { normalizeAngle } from './headAim.js'

/**
 * A provider older than this is ignored (its node was removed or disabled).
 * Generous on purpose: the provider writes once per rendered frame, so the
 * gap between writes IS the frame time — and a throttled tab (hidden pane,
 * busy GPU) can drop to a few fps. A tight window made the steering blink off
 * between frames there; a second only delays the hand-back after a deletion.
 */
export const PROVIDER_STALE_MS = 1000

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

export const lookTargetRuntime = {
  provider: {
    stamp: -Infinity,  // now() at last write; stale after PROVIDER_STALE_MS
    gate: 1,           // 0..1 — scales head + eye follow (0 = hand back to the animation)
    away: 0,           // 0..1 — HEAD: 0 looks at the camera, 1 at the provider's own direction
    eyeAway: 0,        // 0..1 — the same for the EYES (separate, so eyes and head can move at different speeds)
    headYaw: 0,        // body-relative direction for the HEAD (rad)
    headPitch: 0,
    eyeYaw: 0,         // body-relative direction for the EYES (rad)
    eyePitch: 0,
    headSmoothing: 0,  // > 0 overrides Head Follow's smoothing while the provider is live
  },

  scene: {
    valid: false,
    camX: 0, camY: 0, camZ: 0,       // camera world position
    eyeX: 0, eyeY: 0, eyeZ: 0,       // aim origin (eye midpoint) world position
    bodyYaw: 0,                      // body forward yaw in world (rad)
    camYaw: 0,                       // camera direction from the eyes, body-relative (rad)
    camPitch: 0,
    gazeErrorDeg: 180,               // angle between where the eyes point and the camera
    cameraHeld: false,               // the user is dragging the camera (cameraInteraction)
    grabId: 0,                       // changes on every new drag
    cameraMoving: false,             // the camera is moving (a drag, momentum, a scripted move)
  },

  /** True when a provider wrote this frame (or very recently). */
  isLive(t = now()) {
    return t - this.provider.stamp < PROVIDER_STALE_MS
  },

  /** Multiplier for the head/eye follow weights. 1 when no provider is live. */
  gate(t = now()) {
    if (!this.isLive(t)) return 1
    const g = this.provider.gate
    return g < 0 ? 0 : g > 1 ? 1 : g
  },

  /** HEAD: how much to look at the provider's direction instead of the camera. */
  awayAmount(t = now()) {
    if (!this.isLive(t)) return 0
    const a = this.provider.away
    return a < 0 ? 0 : a > 1 ? 1 : a
  },

  /** EYES: how much to look at the provider's direction instead of the camera. */
  eyeAwayAmount(t = now()) {
    if (!this.isLive(t)) return 0
    const a = this.provider.eyeAway
    return a < 0 ? 0 : a > 1 ? 1 : a
  },

  /** Head Follow smoothing override, or 0 for "use the node's own value". */
  headSmoothing(t = now()) {
    return this.isLive(t) ? this.provider.headSmoothing : 0
  },

  /** Forget everything — used on character switch. */
  reset() {
    this.provider.stamp = -Infinity
    this.provider.away = 0
    this.provider.eyeAway = 0
    this.scene.valid = false
  },
}

/**
 * Blend a direction-to-camera with a body-relative yaw/pitch by `away`.
 *
 * Pure and allocation-free: writes a UNIT vector into `out` ({x,y,z}). The
 * blend is a normalized lerp of the two unit directions — the two never point
 * opposite ways here (both lie in front of the character), so nlerp is exact
 * enough and has no slerp singularity to guard.
 *
 * @param {number} cx  direction to the camera (need not be normalized)
 * @param {number} cy
 * @param {number} cz
 * @param {number} bodyYaw  body forward yaw in world (rad)
 * @param {number} yaw      provider yaw, body-relative (rad)
 * @param {number} pitch    provider pitch (rad)
 * @param {number} away     0 = camera, 1 = provider direction
 * @param {{x:number,y:number,z:number}} out
 */
export function blendLookDirection(cx, cy, cz, bodyYaw, yaw, pitch, away, out) {
  const len = Math.hypot(cx, cy, cz) || 1
  let x = cx / len, y = cy / len, z = cz / len
  if (away > 0) {
    const a = away > 1 ? 1 : away
    const wy = bodyYaw + yaw
    const cp = Math.cos(pitch)
    const px = Math.sin(wy) * cp, py = Math.sin(pitch), pz = Math.cos(wy) * cp
    x += (px - x) * a
    y += (py - y) * a
    z += (pz - z) * a
    const l2 = Math.hypot(x, y, z) || 1
    x /= l2; y /= l2; z /= l2
  }
  out.x = x; out.y = y; out.z = z
  return out
}

/**
 * Body-relative yaw/pitch of a world direction. Pure; writes into `out`.
 * @returns {{yaw:number,pitch:number}}
 */
export function bodyRelativeAngles(dx, dy, dz, bodyYaw, out) {
  const len = Math.hypot(dx, dy, dz)
  if (len === 0) { out.yaw = 0; out.pitch = 0; return out }
  out.yaw = normalizeAngle(Math.atan2(dx, dz) - bodyYaw)
  out.pitch = Math.asin(Math.max(-1, Math.min(1, dy / len)))
  return out
}

if (typeof window !== 'undefined') {
  window.__LOOK_TARGET__ = lookTargetRuntime
}
